<script>
import Joi from '@hapi/joi'
import { PaymentMethodService } from '@/services'

export default {
  props: {
    title: {
      type: String,
      default: ''
    },

    dataId: {
      type: Number,
      default: null
    },

    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      form: {
        type: '',
        bank: '',
        accountNo: '',
        accountName: '',
        hasFee: false,
        feePercentage: 0
      },
      types: [],
      banks: [],
      visible: false,
      isLoading: false,
      isSaving: false,
      isDeleting: false
    }
  },

  computed: {
    isInProgress() {
      return this.isLoading || this.isSaving || this.isDeleting
    },

    isEditable() {
      return !this.readonly || !this.isInProgress
    }
  },

  created() {
    if (this.dataId) this.fetch()

    this.fetchTypes()
    this.fetchBanks()
  },

  mounted() {
    this.visible = true
  },

  methods: {
    async fetch() {
      this.isLoading = true

      try {
        const {
          data: { result }
        } = await PaymentMethodService.get(this.dataId)

        this.form = {
          type: result.payment_method_type,
          bank: result.bank,
          accountNo: result.bank_account_id,
          accountName: result.account_name,
          hasFee: result.has_fee,
          feePercentage: +result.fee_percentage
        }
      } catch (e) {
        this.$notify.error(e.message)
      } finally {
        this.isLoading = false
      }
    },

    async fetchTypes() {
      try {
        const {
          data: { result }
        } = await PaymentMethodService.getTypes({ all: true })

        this.types = result
          .filter(item => item.id !== 1)
          .map(item => ({
            id: item.id,
            name: item.name
          }))
      } catch (e) {
        //
      }
    },

    async fetchBanks() {
      try {
        const {
          data: { result }
        } = await PaymentMethodService.getBanks({ all: true })

        this.banks = result.map(item => ({
          id: item.id,
          name: item.name
        }))
      } catch (e) {
        //
      }
    },

    async del() {
      this.isDeleting = true

      try {
        await PaymentMethodService.del(this.dataId)

        this.$emit('delete')

        this.visible = false
      } catch (e) {
        this.$notify.error(e.message)
      } finally {
        this.isDeleting = false
      }
    },

    cancel() {
      this.visible = false
    },

    async save() {
      const validate = () => {
        const schema = Joi.object({
          type: Joi.object(),
          bank: Joi.object(),
          accountNo: Joi.string(),
          accountName: Joi.string(),
          hasFee: Joi.boolean(),
          feePercentage: Joi.number()
        })

        return schema.validateAsync(this.form, { allowUnknown: true })
      }

      const sendData = () => {
        const data = {
          account_name: this.form.accountName,
          fee_percentage: this.form.feePercentage,
          has_fee: this.form.hasFee,
          description: this.form.type.name,
          payment_method_type: this.form.type.id,
          bank: this.form.bank.id,
          bank_account_id: this.form.accountNo
        }

        if (this.dataId) return PaymentMethodService.update(this.dataId, data)

        return PaymentMethodService.create(data)
      }

      try {
        await validate()

        this.isSaving = true

        try {
          await sendData()

          this.$emit('save')

          this.visible = false
        } catch (e) {
          this.$notify.error(e.message)
        } finally {
          this.isSaving = false
        }
      } catch (e) {
        this.$message.warning('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง')
      }
    }
  },

  render() {
    return (
      <el-dialog
        title={this.title}
        visible={this.visible}
        appendToBody
        closeOnClickModal={false}
        closeOnPressEscape={false}
        customClass={this.$style.dialog}
        on={{
          'update:visible': value => {
            this.visible = value
          },
          closed: () => {
            this.$destroy()
          }
        }}
      >
        <el-form
          {...{ directives: [{ name: 'loading', value: this.isLoading }] }}
          labelPosition="top"
        >
          <el-form-item label="ช่องทางชำระ (Payment Type)" required>
            <el-select
              value={this.form.type}
              valueKey="id"
              placeholder="โอนเงิน, บัตรเครดิต, อื่นๆ"
              disabled={this.isEditable}
              style={{ width: '100%' }}
              onChange={value => {
                this.form.type = value
              }}
            >
              {this.types.map((item, index) => (
                <el-option key={index} label={item.name} value={item} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="ธนาคาร (Bank)" required>
            <el-select
              value={this.form.bank}
              valueKey="id"
              placeholder="กรุณาเลือกธนาคาร"
              disabled={this.isEditable}
              style={{ width: '100%' }}
              onChange={value => {
                this.form.bank = value
              }}
            >
              {this.banks.map((item, index) => (
                <el-option key={index} label={item.name} value={item} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="เลขที่บัญชี (Bank ID)" required>
            <el-input
              value={this.form.accountNo}
              disabled={this.isEditable}
              onInput={value => {
                this.form.accountNo = value
              }}
              onChange={value => {
                this.form.accountNo = value.replace(/[^\d]/g, '')
              }}
            />
          </el-form-item>
          <el-form-item
            label="ชื่อบัญชีหรือชื่อช่องทางรับชำระเงิน (Name)"
            required
          >
            <el-input
              value={this.form.accountName}
              disabled={this.isEditable}
              onInput={value => {
                this.form.accountName = value
              }}
            />
          </el-form-item>
          <el-row type="flex" gutter={32} style={{ flexWrap: 'wrap' }}>
            <el-col sm={12}>
              <el-form-item label="มีค่าธรรมเนียม" required>
                <el-switch
                  value={this.form.hasFee}
                  disabled={this.isEditable}
                  onChange={value => {
                    this.form.hasFee = value

                    if (!value) this.form.feePercentage = 0
                  }}
                />
              </el-form-item>
            </el-col>
            <el-col sm={12}>
              <el-form-item required>
                <span slot="label">
                  ค่าธรรมเนียม (%) <small>กำหนดอยู่ระหว่าง 0-5 %</small>
                </span>
                <el-input
                  value={this.form.feePercentage}
                  type="number"
                  min="0"
                  max="5"
                  disabled={!this.form.hasFee || this.isEditable}
                  onInput={value => {
                    this.form.feePercentage = value
                  }}
                  onChange={value => {
                    const convertedValue = Number(value)

                    if (isNaN(convertedValue)) {
                      this.form.feePercentage = 0
                      return
                    }

                    if (convertedValue < 0) this.form.feePercentage = 0
                    else if (convertedValue > 5) this.form.feePercentage = 5
                    else this.form.feePercentage = convertedValue
                  }}
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
        <p>
          ใส่ช่องทางชำระเงิน ถ้าช่องนั้นมี มีธนาคาร ชื่อบัญชี เลขที่บัญชี หรือ
          เลขที่อ้างอิงช่องทางอื่นสามารถใส่ข้อมูลลงมาเพื่อความถูกต้องได้
        </p>
        {!this.readonly && (
          <el-row
            slot="footer"
            type="flex"
            gutter={16}
            justify="center"
            style={{ flexWrap: 'wrap' }}
          >
            {this.dataId && (
              <el-col span={null}>
                <el-button
                  type="danger"
                  loading={this.isDeleting}
                  disabled={this.isInProgress}
                  onClick={this.del}
                >
                  Delete
                </el-button>
              </el-col>
            )}
            <el-col span={null}>
              <el-button disabled={this.isInProgress} onClick={this.cancel}>
                ยกเลิก
              </el-button>
            </el-col>
            <el-col span={null}>
              <el-button
                type="primary"
                loading={this.isSaving}
                disabled={this.isInProgress}
                onClick={this.save}
              >
                บันทึก
              </el-button>
            </el-col>
          </el-row>
        )}
      </el-dialog>
    )
  }
}
</script>

<style lang="scss" module>
.dialog {
  width: auto;
  max-width: 40rem;

  :global(.el-dialog__body) {
    word-break: normal;
  }
}
</style>
